import { useState, useRef, useMemo, useEffect } from 'react';
import { isIntegrationLogsBackendSortingEnabled } from 'helpers/integration_logs_sort_helpers';

// Shared backend-sort orchestration for the lead integration logs grids
// (admin aggregate view + per-integration client view). Owns
// sortColumn/sortDirection, exposes the `remote`/`handleTableChange` props
// react-bootstrap-table needs for backend sorting, and fires `onSortChange`
// (a page-1 refetch) when the sort changes.
export function useIntegrationLogsSort(defaultSortColumn, defaultSortDirection, isFeatureFlagActive, onSortChange) {
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const isInitialSortRender = useRef(true);

  const backendSortingEnabled = useMemo(
    () => isIntegrationLogsBackendSortingEnabled(isFeatureFlagActive),
    [isFeatureFlagActive]
  );

  const remote = useMemo(
    () => (backendSortingEnabled ? { sort: true } : false),
    [backendSortingEnabled]
  );

  const handleTableChange = (type, { sortField, sortOrder }) => {
    if (type !== 'sort' || !backendSortingEnabled) return;
    // Ignore the library's mount-time re-fire of the current sort (the table
    // remounts behind the loading skeleton on every fetch) so we don't trigger
    // a redundant request or fight the caret.
    if (sortField === sortColumn && sortOrder === sortDirection) return;
    // sortField === column dataField; sent verbatim as sort_column.
    setSortColumn(sortField);
    setSortDirection(sortOrder);
  };

  // A sort change resets to page 1 and refetches with the same filters.
  useEffect(() => {
    if (isInitialSortRender.current) {
      isInitialSortRender.current = false;
      return;
    }
    if (!backendSortingEnabled) return;
    onSortChange();
  }, [sortColumn, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps

  return { sortColumn, sortDirection, backendSortingEnabled, remote, handleTableChange };
}
